/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import validate from "../util/validate.js";

var program = {
	code: "s2020",
	name: "Shorebirds",
	description: "The Shorebirds program aims to reinvigorate and coordinate national shorebird population monitoring in Australia to support the conservation and effective management of shorebirds and their habitats.",
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = {
				survey_means: lookups.s2020_survey_means.filter(function(x) { return x.code != 'bicycle'; }),
				wind_speed: lookups.wind_speed,
				wind_direction: lookups.wind_direction,
				tide: lookups.s2020_tide
			};
			return true;
		});
	},
	// options: see load()
	hideSurveyType: true,
	forceSurveyType: "s2020_count",
	fields: function() {
		return {
			surveyMeans: ko.observable(null),
			completeCount: ko.observable(null),
			allShorebirdsVisibleCounted: ko.observable(null),
			completeWaterbirdCount: ko.observable(null),
			windSpeed: ko.observable(null),
			windDirection: ko.observable(null),
			tide: ko.observable(null),
			disturbances: {
				people: ko.observable(null),
				dogsOffLead: ko.observable(null),
				dogsOnLead: ko.observable(null),
				watercraftAtAnchor: ko.observable(null),
				watercraftMoving: ko.observable(null),
				vehicles: ko.observable(null),
				other: ko.observable(null)
			},
			partOfBiannualCount: ko.observable(false),
			lookedForLegBands: ko.observable(null),
			reportedLegBands: ko.observable(null)
		};
	},
	programData: function(survey) {
		var fields = program.fields();
		var validations = program.validations(fields, survey);

		fields.lookedForLegBands.subscribe(function(x) {
			if(!x) {
				fields.reportedLegBands(false);
			}
		});

		return {
			fields: fields,
			validations: validations,
			options: program.options
		};
	},
	updateSurveyJSON: function(json, survey) {
		function code(f) {
			return f == null ? null : f.code;
		}

		function toNumber(x) {
			if(typeof x === "number") {
				return x;
			}
			x = parseInt(x, 10);
			return isNaN(x) ? null : x;
		}

		var fields = survey.programData().s2020.fields;

		json.s2020 = {
			survey_means: code(fields.surveyMeans()),
			complete_count: fields.completeCount(),
			all_shorebirds_visible_counted: fields.allShorebirdsVisibleCounted(),
			complete_waterbird_count: fields.completeWaterbirdCount(),
			wind_speed: code(fields.windSpeed()),
			wind_direction: code(fields.windDirection()),
			tide: code(fields.tide()),
			disturbance_people_count: toNumber(fields.disturbances.people()),
			disturbance_dogs_off_lead_count: toNumber(fields.disturbances.dogsOffLead()),
			disturbance_dogs_on_lead_count: toNumber(fields.disturbances.dogsOnLead()),
			disturbance_watercraft_at_anchor_count: toNumber(fields.disturbances.watercraftAtAnchor()),
			disturbance_watercraft_moving_count: toNumber(fields.disturbances.watercraftMoving()),
			disturbance_vehicles_count: toNumber(fields.disturbances.vehicles()),
			disturbance_other_count: toNumber(fields.disturbances.other()),
			part_of_biannual_count: fields.partOfBiannualCount(),
			looked_for_leg_bands: fields.lookedForLegBands(),
			reported_leg_bands: fields.reportedLegBands()
		};
	},
	loadFromJSON: function(json, fields, survey) {
		var options = this.options;
		var data = json.s2020;

		if(!data) return; //incomplete survey

		function lookup(items, code) {
			return _.findWhere(items, {code: code});
		}

		fields.surveyMeans(lookup(options.survey_means, data.survey_means));
		fields.completeCount(data.complete_count);
		fields.allShorebirdsVisibleCounted(data.all_shorebirds_visible_counted);
		fields.completeWaterbirdCount(data.complete_waterbird_count);

		fields.windSpeed(lookup(options.wind_speed, data.wind_speed));
		fields.windDirection(lookup(options.wind_direction, data.wind_direction));
		fields.tide(lookup(options.tide, data.tide));

		fields.disturbances.people(data.disturbance_people_count);
		fields.disturbances.dogsOffLead(data.disturbance_dogs_off_lead_count);
		fields.disturbances.dogsOnLead(data.disturbance_dogs_on_lead_count);
		fields.disturbances.watercraftAtAnchor(data.disturbance_watercraft_at_anchor_count);
		fields.disturbances.watercraftMoving(data.disturbance_watercraft_moving_count);
		fields.disturbances.vehicles(data.disturbance_vehicles_count);
		fields.disturbances.other(data.disturbance_other_count);

		fields.partOfBiannualCount(data.part_of_biannual_count);

		fields.lookedForLegBands(data.looked_for_leg_bands);
		fields.reportedLegBands(data.reported_leg_bands);
	},
	validations: function(fields, survey) {
		var self = this;
		return [
			validate(fields.allShorebirdsVisibleCounted, function(val) {
				if(val == null) {
					return {
						full: "Must specify whether all visible shorebirds counted",
						brief: "This field is required"
					};
				}
			}),
			validate(fields.completeWaterbirdCount, function(val) {
				if(val == null) {
					return {
						full: "Complete waterbird count must be specified",
						brief: "This field is required"
					};
				}
			}),
			validate(fields.windSpeed, function(val) {
				if(val == null && !survey.existing()) {
					return "Wind speed is required";
				}
			}),
			validate(fields.windDirection, function(val) {
				if(val == null && !survey.existing()) {
					return "Wind direction is required";
				}
			}),
			validate(fields.lookedForLegBands, function(val) {
				if(val == null && !survey.existing()) {
					return "Did you look for leg bands during your survey? is required";
				}
			}),
			validate(fields.reportedLegBands, function(val) {
				if(fields.lookedForLegBands() && val == null && !survey.existing()) {
					return "Were identifiable flagged birds reported to BirdMark? is required";
				}
			}),
			// Wrapping in a pureComputed so we don't overwrite existing validation on surveyPoint
			validate(ko.pureComputed(function() { return survey.surveyPoint(); }), self.validateSurveyPoint, {tab: 'location'})
		];
	},
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 's2020_count',
			name: 'Shorebird Count'
		});
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	validateSurveyPoint: function(surveyPoint) {
		if(!surveyPoint || !surveyPoint.sharedSites()) return;
		var withinShorebirdSite = _.any(surveyPoint.sharedSites(), function(site) {
			return site.layer_id == 1; // TODO: fix hard coded layer ID
		});
		if(!withinShorebirdSite) {
			return "Invalid location selected: Survey point must lie within a Shorebird Count Area (purple polygon). If you would like to create a new Shorebird count area please contact shorebirds@birdlife.org.au";
		}
	},
	visibleSharedSiteLayers: () => ["s2020"],
	defaultAreaLayer: function(layers) {
		return _.find(layers, function(layer) {
			return layer.code == "s2020";
		});
	},
	surveyTypeOptions: [],
	observerCountIsOptional: function(survey) {
		return survey.existing();
	}
};

export default program;
